import { ScoringResult } from './QuizScoringService'

type ServerItem = {
  [key: string]: any
  question_id: string
  type?: string
  group?: 'TN' | 'DS' | 'TL' | 'TLU'
  is_correct: boolean
  points: number
  max_points: number
  dap_an_dung?: any
  answer_a?: any
  answer_b?: any
  answer_c?: any
  answer_d?: any
  loi_giai?: string
  solution_a?: string
  solution_b?: string
  solution_c?: string
  solution_d?: string
}

export type PmakerServerResult = {
  score: number
  max_score: number
  percentage: number
  passed: boolean
  feedback: string
  section_scores: Record<'TN' | 'DS' | 'TL' | 'TLU', {
    score: number
    max_score: number
    count: number
  }>
  results: ServerItem[]
}

const LETTERS = ['A', 'B', 'C', 'D']

export function mapPmakerAnswersForSubmit(
  answers: Record<string, any>,
  questions: any[]
): Record<string, any> {
  const serverAnswers = { ...answers }

  for (const question of questions) {
    const type = String(question.type || question.loai || '').toLowerCase()
    const isMultipleChoice = type.includes('trac_nghiem')
      || type.includes('multiple_choice')
      || String(question.examMetadata?.group || '').toUpperCase() === 'TN'
    if (!isMultipleChoice || !Array.isArray(question.lua_chon)) continue

    const picked = serverAnswers[question.id]
    if (typeof picked !== 'string') continue

    const selectedOptionIndex = question.lua_chon.findIndex((choice: any) =>
      choice && typeof choice === 'object'
      && choice.id != null
      && String(choice.id) === picked
    )
    if (selectedOptionIndex >= 0) {
      const choice = question.lua_chon[selectedOptionIndex]
      const content = choice.content ?? choice.text ?? ''
      serverAnswers[question.id] = {
        selected_option_id: picked,
        display_letter: LETTERS[selectedOptionIndex],
        content: String(content),
      }
      continue
    }

    const displayLetter = picked.trim().toUpperCase()
    const index = displayLetter.charCodeAt(0) - 65
    if (index < 0 || index >= question.lua_chon.length) continue

    const choice = question.lua_chon[index]
    const content = choice && typeof choice === 'object'
      ? (choice.text ?? choice.content)
      : choice
    const originalLetter = Array.isArray(question.shuffleLetterMap)
      ? question.shuffleLetterMap[index]
      : displayLetter

    serverAnswers[question.id] = {
      display_letter: displayLetter,
      original_letter: originalLetter || displayLetter,
      content: content == null ? '' : String(content),
    }
  }

  return serverAnswers
}

function displayCorrectAnswer(question: any, item: ServerItem): any {
  if (Array.isArray(question?.lua_chon)) {
    const index = question.lua_chon.findIndex((choice: any) =>
      choice && typeof choice === 'object'
      && choice.id != null
      && String(choice.id) === String(item.dap_an_dung)
    )
    if (index >= 0) return LETTERS[index]
  }
  if (Array.isArray(question?.shuffleLetterMap)) {
    const index = question.shuffleLetterMap.indexOf(item.dap_an_dung)
    return index >= 0 ? LETTERS[index] : item.dap_an_dung
  }
  if (item.group === 'DS' || question?.type === 'dung_sai') {
    return {
      a: item.answer_a,
      b: item.answer_b,
      c: item.answer_c,
      d: item.answer_d
    }
  }
  return item.dap_an_dung
}

export function applyPmakerServerResult(
  localResult: ScoringResult,
  questions: any[],
  server: PmakerServerResult
): ScoringResult {
  if (!server || !Array.isArray(server.results)) {
    throw new Error('Backend không trả dữ liệu chấm chi tiết')
  }

  const byId = new Map(server.results.map(item => [item.question_id, item]))
  if (byId.size !== questions.length) {
    throw new Error(`Kết quả backend thiếu câu hỏi (${byId.size}/${questions.length})`)
  }

  const questionById = new Map(questions.map(question => [question.id, question]))
  for (const question of questions) {
    const item = byId.get(question.id)
    if (!item) throw new Error(`Thiếu kết quả câu ${question.id}`)

    const correctAnswer = displayCorrectAnswer(question, item)
    if (correctAnswer != null && item.group !== 'DS') question.dap_an_dung = correctAnswer
    question.diem = item.max_points
    if (item.loi_giai != null) question.loi_giai = item.loi_giai
    ;(['a', 'b', 'c', 'd'] as const).forEach(letter => {
      if (item[`answer_${letter}`] != null) question[`answer_${letter}`] = item[`answer_${letter}`]
      if (item[`solution_${letter}`] != null) question[`solution_${letter}`] = item[`solution_${letter}`]
    })
  }

  const answers = localResult.answers.map(answer => {
    const item = byId.get(answer.questionId)
    const question = questionById.get(answer.questionId)
    if (!item) throw new Error(`Thiếu kết quả câu ${answer.questionId}`)
    return {
      ...answer,
      correctAnswer: displayCorrectAnswer(question, item),
      isCorrect: Boolean(item.is_correct),
      score: Number(item.points),
      maxScore: Number(item.max_points),
      points: Number(item.points),
      explanation: item.loi_giai ?? answer.explanation
    }
  })

  const detailedAnalysis = localResult.detailedAnalysis.map(analysis => {
    const item = byId.get(analysis.questionId)
    const question = questionById.get(analysis.questionId)
    if (!item) throw new Error(`Thiếu kết quả câu ${analysis.questionId}`)
    return {
      ...analysis,
      correctAnswer: displayCorrectAnswer(question, item),
      isCorrect: Boolean(item.is_correct),
      points: Number(item.points),
      explanation: item.loi_giai ?? analysis.explanation
    }
  })

  const sectionScores = (['TN', 'DS', 'TL', 'TLU'] as const).reduce((output, group) => {
    const section = server.section_scores?.[group]
    output[group] = {
      score: Number(section?.score || 0),
      maxScore: Number(section?.max_score || 0),
      count: Number(section?.count || 0)
    }
    return output
  }, {} as ScoringResult['sectionScores'])

  return {
    score: Number(server.score),
    maxScore: Number(server.max_score),
    percentage: Number(server.percentage),
    passed: Boolean(server.passed),
    feedback: server.feedback || `Kết quả: ${server.score}/${server.max_score} điểm.`,
    answers,
    detailedAnalysis,
    sectionScores
  }
}
